import React, { useState, useEffect, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useLocation } from 'react-router-dom';
import { Topic } from 'types/topic';
import chatAPI from 'apis/chatAPI';
import sessionAPI from 'apis/sessionAPI';
import uploadAPI from 'apis/uploadAPI';
import { useFetchData } from 'hooks/data';
import { ChatRequest } from 'types/chat';
import { SessionHistory, MessageHistory, MessageHistoryResponse } from 'types/session';
import Bubble from 'components/utils/Bubble';
import authAPI from 'apis/authAPI';
import { modifyNumberedLists } from 'libs/utils';
import ChatInput from './ChatInput';
import { UploadedFileInfo } from 'types/upload';
import Modal from 'components/utils/Modal';
import EditableSessionName from './EditableSessionName';
import { CHAT_MODE, ENABLE_TOPIC } from 'config';

const CHARACTER_LIMIT = 5000;

type ChatProps = {
  userId: string;
  userRole: string;
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  sessionName: string;
  setSessionName: (sessionName: string) => void;
  topics: Topic[];
  selectedTopic: string;
  messages: MessageHistory[],
  setMessages: React.Dispatch<React.SetStateAction<MessageHistory[]>>;
  sessionList: SessionHistory[];
  setSessionList: React.Dispatch<React.SetStateAction<SessionHistory[]>>;
  setBlock: React.Dispatch<React.SetStateAction<boolean>>;
  scrollBarColor?: string;
  useTAS: boolean;
  useTASExample: boolean;
  useTSA: boolean;
  isGuardrailEnabled: boolean;
};

const getSelectedAgent = (topics: Topic[], selectedTopic: string) => {
  const filteredTopic = topics.find(
    (item) => item.name.toUpperCase() === selectedTopic.toUpperCase(),
  );
  return filteredTopic?.agent || 'reason';
};

const renderMessageAsText = (
  index: number,
  messageHistory: MessageHistory,
  currentSessionId: string,
  isDisplayFeedback: boolean,
  onFeedbackUpdate: (messageIndex: number, feedback: { rating: 'positive' | 'negative' | null; comment?: string } | null) => void
) => {
  const isUserMessage = messageHistory.chatType === 'user';
  const message = modifyNumberedLists(messageHistory.message);
  const messageIndex = messageHistory.index == undefined ? Math.max(0, Math.round((index / 2) - 1)) : messageHistory.index;

  return (
    <div key={`${messageHistory.chatType}-${index}`} className={`flex mb-4 ${isUserMessage ? 'justify-end' : 'justify-start'}`}>
      <Bubble
        message={message}
        index={messageIndex} 
        isLoading={messageHistory.isLoading} 
        isUserMessage={isUserMessage}
        isMarkdown={true} 
        uploadFiles={messageHistory.uploadFiles ?? []}
        chunkId={messageHistory.chunkId}
        sessionId={currentSessionId}
        feedback={messageHistory.feedback}
        isDisplayFeedback={isDisplayFeedback}
        onFeedbackUpdate={(feedback) => onFeedbackUpdate(index, feedback)}
      />
    </div>
  );
};

const Chat: React.FC<ChatProps> = ({
  userId,
  userRole,
  sessionId,
  setSessionId,
  sessionName,
  setSessionName,
  topics,
  selectedTopic,
  messages,
  setMessages,
  sessionList,
  setSessionList,
  setBlock,
  scrollBarColor = 'scrollbar-thumb-BAMPink',
  useTAS,
  useTASExample,
  useTSA,
  isGuardrailEnabled
}) => {
  const location = useLocation();
  const selectedAgent = getSelectedAgent(topics, selectedTopic);

  const { data: messageHistoryData } = useFetchData<MessageHistoryResponse>(
    sessionAPI.getMessageHistory,
    [sessionId, CHAT_MODE],
  );

  const isDisplayFeedback = userRole && userRole !== "student" ? true : false;

  const [blockInput, setBlockInput] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [uploadFileData, setUploadFileData] = useState<UploadedFileInfo[]>([]);
  const [textAreaFocus, setTextAreaFocus] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [errorModal, setErrorModal] = useState<{ open: boolean; message: string }>({ open: false, message: '' });
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (messageHistoryData && messageHistoryData.messages) {
      setMessages(messageHistoryData.messages);
    }
    setSelectedFiles([])
    setUploadFileData([])
  }, [messageHistoryData, sessionId]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const newHeight = Math.max(
        50,
        Math.min(textareaRef.current.scrollHeight, 300),
      );
      textareaRef.current.style.height = `${newHeight}px`;
    }
  }, [inputValue]);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
    sessionList.forEach((session) => {
      if (session.sessionId === sessionId) {
        setSessionName(session.sessionName);
      }
    })
  }, [messages, sessionId]);

  const handleFeedbackUpdate = (messageIndex: number, feedback: { rating: 'positive' | 'negative' | null; comment?: string } | null) => {
    setMessages(prevMessages => {
      const updatedMessages = [...prevMessages];
      const actualIndex = updatedMessages.length - 1 - messageIndex;
      if (actualIndex >= 0 && actualIndex < updatedMessages.length) {
        updatedMessages[actualIndex] = {
          ...updatedMessages[actualIndex],
          feedback: feedback === null || feedback.rating === null ? null : {
            rating: feedback.rating,
            comment: feedback.comment
          }
        };
      }
      return updatedMessages;
    });
  };

  const handleSessionNameUpdate = async (sessionId: string, newName: string) => {
    try {
      const { data, statusCode } = await sessionAPI.updateSessionName(sessionId, newName, CHAT_MODE);
      if (statusCode === 200) {
        const updatedSessionList = sessionList.map(session => 
          session.sessionId === sessionId 
            ? { ...session, sessionName: newName } 
            : session
        );
        setSessionList(updatedSessionList);
      }
    } catch (error) {
      console.error("Failed to update session name:", error);
    }
  };

  const handleSubmit = async () => {
    if (inputValue.trim()) {
      setBlockInput(true);
      setBlock(true);

      let uploadedFiles: UploadedFileInfo[] = [];
      if (selectedFiles.length > 0) {
        setUploading(true);
        try {
          const { data, statusCode, error } = await uploadAPI.uploadFiles({
            sessionId,
            files: selectedFiles
          });
          if (statusCode !== 200 || !data || (Array.isArray(data) && data.length !== selectedFiles.length)) {
            let errorMessage = 'File upload failed. Please check your files and try again.';
            if (typeof error === 'string') {
              errorMessage = error;
            } else if (error && typeof error === 'object' && 'message' in error && typeof (error as any).message === 'string') {
              errorMessage = (error as any).message;
            }
            setErrorModal({
              open: true,
              message: errorMessage
            });
            setUploading(false);
            setBlockInput(false);
            setBlock(false);
            return;
          }
          uploadedFiles = data;
        } catch (err: any) {
          setErrorModal({
            open: true,
            message:
              typeof err?.response?.data === 'string'
                ? err.response.data
                : err?.response?.data?.message || 'File upload failed. Please try again.'
          });
          setUploading(false);
          setBlockInput(false);
          setBlock(false);
          return;
        }
        setUploading(false);
      }

      const userMessage: MessageHistory = {
        chatType: 'user',
        message: inputValue,
        uploadFiles: uploadedFiles.length > 0 ? uploadedFiles : [],
      };
      const botTypingMessage: MessageHistory = {
        chatType: 'agent',
        message: '',
        isLoading: true,
        uploadFiles: [],
      };
      setMessages((prevMessages: MessageHistory[]) => [...prevMessages, userMessage]);

      setInputValue('');
      setBlockInput(true);
      setBlock(true);

      setTimeout(() => {
        setMessages((prevMessages: MessageHistory[]) => {
          if (
            prevMessages.length > 0 &&
            prevMessages[prevMessages.length - 1].chatType === 'user'
          ) {
            return [...prevMessages, botTypingMessage];
          } else {
            return [...prevMessages];
          }
        });
      }, 500);

      try {
        const chatRequest: ChatRequest = {
          userId,
          sessionId,
          sessionName,
          ...(ENABLE_TOPIC ? { topic: selectedTopic } : {}),
          userMessage: inputValue,
          agent: selectedAgent,
          uploadFiles: uploadedFiles,
          useTas: useTAS,
          useTasExample: useTASExample,
          useTsa: useTSA,
          guardrailEnabled: isGuardrailEnabled,
        };
        
        let chatResponse = CHAT_MODE === 'hintbot'
          ? await chatAPI.hint(chatRequest)
          : await chatAPI.chat(chatRequest);

        // Recover when backend rejects reused session id with a different topic.
        if (chatResponse.error === 'Session topic conflict') {
          const retrySessionId = uuidv4();
          const retrySessionName = retrySessionId;
          const retryRequest: ChatRequest = {
            ...chatRequest,
            sessionId: retrySessionId,
            sessionName: retrySessionName,
          };

          setSessionId(retrySessionId);
          setSessionName(retrySessionName);

          chatResponse = CHAT_MODE === 'hintbot'
            ? await chatAPI.hint(retryRequest)
            : await chatAPI.chat(retryRequest);
        }

        const { data: botResponse, statusCode } = chatResponse;

        const { data: response } = await authAPI.checkLoginStatus()
        if (response && response.isLoggedIn) {
          localStorage.setItem('token', response.accessToken);
        }

        const botMessage: MessageHistory = {
          chatType: 'agent',
          chunkId: botResponse ? botResponse.chunkId : undefined,
          message:
            statusCode === 200 && botResponse
              ? botResponse.responseMessage
              : 'Unable to connect to the server. Please try again later.',
          uploadFiles: [],
        };
        setSelectedFiles([]);
        setUploadFileData([]);

        setMessages((prevMessages: MessageHistory[]) => {
          if (
            prevMessages.length > 0 &&
            prevMessages[prevMessages.length - 1].isLoading === true
          ) {
            return [...prevMessages.slice(0, -1), botMessage];
          } else {
            return [...prevMessages, botMessage];
          }
        });

        const sessionExists = sessionList.some(session => session.sessionId === sessionId);
        if (inputValue) {
          if (!sessionExists) {
            setSessionList([{sessionId, sessionName, userMessage: inputValue}, ...sessionList]);
          } else {
            const currentSession = sessionList.find(session => session.sessionId === sessionId);
            if (currentSession && currentSession.userMessage !== inputValue) {
              const updatedSessionList = sessionList.filter(session => session.sessionId !== sessionId);
              const updatedSession = {
                sessionId,
                sessionName,
                userMessage: inputValue
              };
              setSessionList([updatedSession, ...updatedSessionList]);
            }
          }
        }
      } finally {
        setBlockInput(false);
        setBlock(false)
      }
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter') {
      if (inputValue.length > CHARACTER_LIMIT) {
        e.preventDefault();
      } else if (!e.shiftKey) {
        e.preventDefault();
        handleSubmit();
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files);
      const existingFileNames = new Set(selectedFiles.map(file => file.name));
      const uniqueNewFiles = newFiles.filter(newFile => !existingFileNames.has(newFile.name));
      if (uniqueNewFiles.length > 0) {
        setSelectedFiles([...selectedFiles, ...uniqueNewFiles]);
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleAttachClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleRemoveFile = (fileToRemove: File) => {
    setSelectedFiles(selectedFiles.filter(file => file !== fileToRemove));
    setUploadFileData(uploadFileData.filter(data => data.fileName !== fileToRemove.name));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="h-screen flex flex-col">
      <EditableSessionName 
        sessionId={sessionId}
        sessionName={sessionName}
        setSessionName={setSessionName}
        disabled={blockInput}
        onSessionNameUpdate={handleSessionNameUpdate}
      />
      <div className="max-w-full h-[93%] flex flex-col rounded-[6px]">
        <div ref={chatContainerRef} className={`flex-grow px-8 pt-30 flex flex-col-reverse overflow-y-auto scrollbar-thin scrollbar-track-BALGray ${scrollBarColor}`}>
          {messageHistoryData &&
            messages
              .slice()
              .reverse()
              .map((item: MessageHistory, index: number) => {
                const reversedIndex = messages.length - 1 - index;
                return renderMessageAsText(reversedIndex, item, sessionId, isDisplayFeedback, handleFeedbackUpdate);
              })}
        </div>
        
        <ChatInput
          inputValue={inputValue}
          setInputValue={setInputValue}
          textAreaFocus={textAreaFocus}
          setTextAreaFocus={setTextAreaFocus}
          blockInput={blockInput}
          handleSubmit={handleSubmit}
          handleInputChange={handleInputChange}
          handleKeyDown={handleKeyDown}
          handleAttachClick={handleAttachClick}
          handleFileInputChange={handleFileInputChange}
          selectedFiles={selectedFiles}
          setSelectedFiles={setSelectedFiles}
          textareaRef={textareaRef}
          fileInputRef={fileInputRef}
          handleRemoveFile={handleRemoveFile}
          uploading={uploading}
        />
        {errorModal.open && (
          <Modal
            open={errorModal.open}
            onClose={() => setErrorModal({ open: false, message: '' })}
            title="File Upload Error"
          >
            <div className='text-sm'>
              {errorModal.message.split('\n').map((line, idx) => (
                <div key={idx} className='py-1'>{line}</div>
              ))}
            </div>
          </Modal>
        )}
      </div>
    </div>
  );
};

export default Chat;